import * as THREE from 'three'
import { RAPIER } from '../physics'
import { toEngineCoordinates, type SourceVector3 } from './bsp-coordinates'
import { createDoorInstance, type SlidingDoor } from '../world/doors'
import { DestructibleManager, type DestructibleObject } from '../world/destructibles'
import { type ElevatorController } from '../world/elevator'
import { PhysicsSceneBuilder } from './physics-scene-builder'
import {
  createAuthenticWallMaterial,
  createAuthenticFloorMaterial,
  createAuthenticCeilingMaterial,
  createTargetBullseyeTexture,
} from './textures'
import { GltfPropLoader } from './gltf-prop-loader'
import type { DebugMaterialRegistry } from '../debug/debug-materials'

export interface BspPlacedObject {
  tag: string
  entity_class?: string
  piece_name?: string
  offset?: number
  centroid: SourceVector3
  bbox_min: SourceVector3
  bbox_max: SourceVector3
  dimensions_source?: {
    span_x: number
    span_y: number
    height_z: number
    door_width: number
    door_depth: number
    leaf_depth?: number
    clearance_margin?: number
    slide_axis: 'x' | 'z'
  }
}

export interface BspSpawnPoint {
  position: SourceVector3
  facing_rad?: number
}

export interface BspSectorData {
  plane_normal: [number, number, number]
  plane_d: number
  bbox_min: SourceVector3
  bbox_max: SourceVector3
  face_count: number
}

export interface BspLevelData {
  level: string
  spawn_point: BspSpawnPoint
  mesh_point_cloud?: {
    count?: number
    vertex_count?: number
    bbox_min: SourceVector3
    bbox_max: SourceVector3
    vertices?: SourceVector3[]
  }
  sectors?: BspSectorData[]
  placed_objects?: BspPlacedObject[]
  tutorial_messages?: Array<{
    text: string
    triggerObjectId?: string | null
    nearby_tags?: string[]
  }>
}

export interface BspLoadedLevel {
  spawnPosition: THREE.Vector3
  facingRad: number
  roomMeshes: THREE.Mesh[]
  doors: SlidingDoor[]
  destructibles: DestructibleObject[]
  destructibleManager: DestructibleManager
  elevators: ElevatorController[]
  colliders: RAPIER.Collider[]
  targets: THREE.Mesh[]
  messages: Array<{
    text: string
    triggerObjectId?: string | null
  }>
}

export class BspLevelLoader {
  private readonly scene: THREE.Scene
  private readonly world: RAPIER.World
  private readonly scale: number
  private readonly debugRegistry?: DebugMaterialRegistry
  private readonly builder: PhysicsSceneBuilder
  private readonly destructibleManager: DestructibleManager
  private readonly propLoader: GltfPropLoader

  constructor(
    scene: THREE.Scene,
    world: RAPIER.World,
    scale = 0.05,
    debugRegistry?: DebugMaterialRegistry,
  ) {
    this.scene = scene
    this.world = world
    this.scale = scale
    this.debugRegistry = debugRegistry
    this.builder = new PhysicsSceneBuilder(scene, world, RAPIER, debugRegistry)
    this.destructibleManager = new DestructibleManager(scene)
    this.propLoader = new GltfPropLoader(scene, world, this.destructibleManager, debugRegistry)
  }

  getPropLoader(): GltfPropLoader {
    return this.propLoader
  }

  async load(data: BspLevelData): Promise<BspLoadedLevel> {
    const lvl = (data.level || '').toLowerCase()
    if (lvl === 'ab00') {
      return await this.loadAb00Sector(data)
    }
    if (lvl === 'tutorial' || lvl === 'training') {
      return await this.loadTrainingSector(data)
    }
    return await this.loadGenericSector(data)
  }

  private async loadAb00Sector(data: BspLevelData): Promise<BspLoadedLevel> {
    const doors: SlidingDoor[] = []
    const elevators: ElevatorController[] = []
    const targets: THREE.Mesh[] = []
    const destructibleManager = new DestructibleManager(this.scene)
    const destructibles: DestructibleObject[] = []

    const wallMat = createAuthenticWallMaterial()
    const floorMat = createAuthenticFloorMaterial()
    const ceilingMat = createAuthenticCeilingMaterial()

    // Sector dimensions from TREES.E3:ab00.e3
    const W = 2.25
    const L = 5.91
    const H = 3.25
    const T = 0.35
    const Y = H / 2

    // 1. Floor & Ceiling
    this.builder.addFloor({ width: W, length: L, position: { x: 0, y: 0, z: 0 }, material: floorMat, name: 'Airlock Floor Panel', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3 (Ground Floor)' })
    this.builder.addCeiling({ width: W, length: L, position: { x: 0, y: H, z: 0 }, material: ceilingMat, name: 'Airlock Ceiling Panel', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3 (Ceiling)' })

    // 2. West and East Perimeter Walls
    this.builder.addWall({ width: T, height: H, depth: L, position: { x: -W / 2, y: Y, z: 0 }, material: wallMat, name: 'Airlock West Wall', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3' })
    this.builder.addWall({ width: T, height: H, depth: L, position: { x: W / 2, y: Y, z: 0 }, material: wallMat, name: 'Airlock East Wall', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3' })

    // 3. North & South End Walls
    this.builder.addWall({ width: W, height: H, depth: T, position: { x: 0, y: Y, z: -L / 2 }, material: wallMat, name: 'North Airlock Portal Wall', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3' })
    this.builder.addWall({ width: W, height: H, depth: T, position: { x: 0, y: Y, z: L / 2 }, material: wallMat, name: 'South Airlock Portal Wall', sourceTag: 'ESOTERIA/TREES.E3 : ab00.e3' })

    // 4. Diegetic Lighting
    const ambientLight = new THREE.AmbientLight(0x7a8ea3, 2.2)
    this.scene.add(ambientLight)

    const airlockLight = new THREE.PointLight(0x00f0ff, 4.5, 8)
    airlockLight.position.set(0, H - 0.5, 0)
    this.scene.add(airlockLight)

    // 5. Props
    try {
      const gltfProps = await this.propLoader.loadSectorProps('ab00')
      doors.push(...gltfProps.doors)
      destructibles.push(...gltfProps.destructibles)
      elevators.push(...gltfProps.elevators)
      this.builder.colliders.push(...gltfProps.colliders)
      this.builder.meshes.push(...gltfProps.meshes)
    } catch {
      // Fallback handled gracefully
    }

    const spawnPosition = new THREE.Vector3(0, 1.2, 0)
    const facingRad = 0.0

    return {
      spawnPosition,
      facingRad,
      roomMeshes: this.builder.meshes,
      doors,
      destructibles,
      destructibleManager,
      elevators,
      colliders: this.builder.colliders,
      targets,
      messages: data.tutorial_messages ?? [],
    }
  }

  private async loadTrainingSector(data: BspLevelData): Promise<BspLoadedLevel> {
    const spawnPosition = new THREE.Vector3(0, 1.2, -2.0)
    const facingRad = Math.PI

    const doors: SlidingDoor[] = []
    const elevators: ElevatorController[] = []
    const targets: THREE.Mesh[] = []
    const destructibleManager = new DestructibleManager(this.scene)
    const destructibles: DestructibleObject[] = []

    const wallMat = createAuthenticWallMaterial()
    const floorMat = createAuthenticFloorMaterial()
    const ceilingMat = createAuthenticCeilingMaterial()
    const targetTex = typeof document !== 'undefined' ? createTargetBullseyeTexture() : null

    // 1. Facility Ground Floor & Ceiling
    this.builder.addFloor({ width: 24, length: 16, position: { x: -2, y: 0, z: 1 }, material: floorMat, name: 'Main Training Floor', sourceTag: 'ESOTERIA/TRAINING.E3 : Ground Floor' })
    this.builder.addCeiling({ width: 24, length: 16, position: { x: -2, y: 3.6, z: 1 }, material: ceilingMat, name: 'Ceiling Sector Panel', sourceTag: 'ESOTERIA/TRAINING.E3 : Ceiling Boundary' })

    const H = 3.6
    const Y = H / 2
    const T = 0.35

    // 2. Perimeter Outer Boundaries
    this.builder.addWall({ width: 20, height: H, depth: T, position: { x: -2, y: Y, z: 8.0 }, material: wallMat, name: 'South Perimeter Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Outer South Boundary' })
    this.builder.addWall({ width: T, height: H, depth: 8.89, position: { x: -12, y: Y, z: 3.555 }, material: wallMat, name: 'West Range Perimeter Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Outer West Boundary' })
    this.builder.addWall({ width: T, height: H, depth: 8.89, position: { x: 8, y: Y, z: 3.555 }, material: wallMat, name: 'East Circuit Hub Perimeter Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Outer East Boundary' })
    this.builder.addWall({ width: 10, height: H, depth: T, position: { x: -7, y: Y, z: -0.89 }, material: wallMat, name: 'West Wing North Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : West Wing Wall' })
    this.builder.addWall({ width: 6, height: H, depth: T, position: { x: 5, y: Y, z: -0.89 }, material: wallMat, name: 'East Wing North Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : East Wing Wall' })
    this.builder.addWall({ width: 4, height: H, depth: T, position: { x: 0, y: Y, z: -6.0 }, material: wallMat, name: 'Launch Bay North Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Launch Airlock North Wall' })
    this.builder.addWall({ width: T, height: H, depth: 5.11, position: { x: -2, y: Y, z: -3.445 }, material: wallMat, name: 'Launch Bay West Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Launch Airlock West Wall' })
    this.builder.addWall({ width: T, height: H, depth: 5.11, position: { x: 2, y: Y, z: -3.445 }, material: wallMat, name: 'Launch Bay East Wall', sourceTag: 'ESOTERIA/TRAINING.E3 : Launch Airlock East Wall' })

    // 3. Central Divider Walls
    this.builder.addWall({ width: T, height: H, depth: 4.5, position: { x: -2.07, y: Y, z: 1.5 }, material: wallMat, name: 'West Corridor Divider (North)', sourceTag: 'ESOTERIA/TRAINING.E3 : West Corridor Portal Frame' })
    this.builder.addWall({ width: T, height: H, depth: 2.5, position: { x: -2.07, y: Y, z: 6.75 }, material: wallMat, name: 'West Corridor Divider (South)', sourceTag: 'ESOTERIA/TRAINING.E3 : West Corridor Portal Frame' })
    this.builder.addWall({ width: T, height: H, depth: 4.5, position: { x: 2.03, y: Y, z: 1.5 }, material: wallMat, name: 'East Corridor Divider (North)', sourceTag: 'ESOTERIA/TRAINING.E3 : East Corridor Portal Frame' })
    this.builder.addWall({ width: T, height: H, depth: 2.5, position: { x: 2.03, y: Y, z: 6.75 }, material: wallMat, name: 'East Corridor Divider (South)', sourceTag: 'ESOTERIA/TRAINING.E3 : East Corridor Portal Frame' })

    // 4. Range Partitions
    this.builder.addWall({ width: T, height: H, depth: 3, position: { x: -5.07, y: Y, z: 0.5 }, material: wallMat, name: 'Target Gallery North Partition', sourceTag: 'ESOTERIA/TRAINING.E3 : Shooting Range Divider' })
    this.builder.addWall({ width: T, height: H, depth: 2, position: { x: -5.07, y: Y, z: 7.0 }, material: wallMat, name: 'Target Gallery South Partition', sourceTag: 'ESOTERIA/TRAINING.E3 : Shooting Range Divider' })

    // 5. Trims
    this.builder.addTrim({ width: 4, height: 0.04, depth: 0.15, position: { x: 0, y: 0.02, z: -0.89 }, color: 0x00f0ff, name: 'Cyan Launch Blast Door Line' })
    this.builder.addTrim({ width: 0.15, height: 0.04, depth: 2, position: { x: -2.07, y: 0.02, z: 4.54 }, color: 0xffaa00, name: 'Amber Target Range Portal Line' })
    this.builder.addTrim({ width: 0.15, height: 0.04, depth: 2, position: { x: 2.03, y: 0.02, z: 4.54 }, color: 0x00ff88, name: 'Green Circuit Hub Portal Line' })
    this.builder.addTrim({ width: 4, height: 0.04, depth: 0.15, position: { x: 0, y: 0.02, z: 5.45 }, color: 0x00f0ff, name: 'Cyan Swim Sector Door Line' })

    // 6. Lights
    const ambientLight = new THREE.AmbientLight(0x7a8ea3, 2.0)
    this.scene.add(ambientLight)

    const dirLight = new THREE.DirectionalLight(0xddeeff, 2.8)
    dirLight.position.set(0, 10, 2)
    this.scene.add(dirLight)

    const spawnLight = new THREE.PointLight(0x00f0ff, 4.0, 10)
    spawnLight.position.set(0, 3.0, 0)
    this.scene.add(spawnLight)

    const targetLight = new THREE.PointLight(0xffaa00, 5.0, 14)
    targetLight.position.set(-8, 3.0, 4.5)
    this.scene.add(targetLight)

    const circuitLight = new THREE.PointLight(0x00ff88, 5.0, 14)
    circuitLight.position.set(5.5, 3.0, 5.5)
    this.scene.add(circuitLight)

    // 7. Targets
    const targetMat = new THREE.MeshStandardMaterial({
      map: targetTex,
      color: 0xffffff,
      roughness: 0.3,
      metalness: 0.5,
    })
    const targetPositions = [
      new THREE.Vector3(-9.57, 1.2, 5.52),
      new THREE.Vector3(-9.57, 1.2, 3.50),
      new THREE.Vector3(-9.57, 1.2, 4.50),
    ]
    for (let i = 0; i < targetPositions.length; i++) {
      const pos = targetPositions[i]
      const targetMesh = this.builder.addTarget({
        radius: 0.5,
        height: 1.6,
        position: pos,
        material: targetMat,
        name: `Range Target #${i + 1}`,
        sourceTag: 'ESOTERIA/AREAS.E3 : CSlidingDoor piece: mvtarget',
      })
      targets.push(targetMesh)
    }

    // 8. Authentic Sector Props & Entities from training_props.glb
    let gltfLoaded = false
    try {
      const gltfProps = await this.propLoader.loadSectorProps('training')
      if (gltfProps.doors.length > 0 || gltfProps.destructibles.length > 0 || gltfProps.meshes.length > 0) {
        doors.push(...gltfProps.doors)
        destructibles.push(...gltfProps.destructibles)
        elevators.push(...gltfProps.elevators)
        this.builder.colliders.push(...gltfProps.colliders)
        this.builder.meshes.push(...gltfProps.meshes)
        gltfLoaded = true
      }
    } catch {
      // In-memory test fixture fallback below
    }

    if (!gltfLoaded && data.placed_objects && data.placed_objects.length > 0) {
      for (const obj of data.placed_objects) {
        const objPos = toEngineCoordinates(obj.centroid, [-3483.625, -3221.71875, 1183.25], this.scale)
        if (obj.entity_class === 'CSlidingDoor' || obj.tag.includes('CSlidingDoor')) {
          const dims = obj.dimensions_source ?? {
            door_width: 3.5,
            door_depth: 0.35,
            slide_axis: 'x' as const,
          }
          const doorWidth = dims.door_width > 5.0 ? dims.door_width * this.scale * 2 : (dims.door_width || 3.5)
          const doorDepth = dims.door_depth > 1.0 ? dims.door_depth * this.scale : (dims.door_depth || 0.35)
          const door = createDoorInstance(this.scene, this.world, RAPIER, {
            id: obj.piece_name || obj.tag,
            position: new THREE.Vector3(objPos.x, 0.0, objPos.z),
            width: doorWidth,
            height: 3.4,
            depth: doorDepth,
            clearanceMargin: 0.02,
            slideAxis: dims.slide_axis,
            slideDistance: 2.0,
          })
          doors.push(door)
          const doorMeta = {
            id: obj.piece_name || obj.tag,
            name: `${obj.piece_name || 'Sliding'} Door`,
            sourceTag: `ESOTERIA/AREAS.E3 : ${obj.tag}`,
            dimensions: [doorWidth, 3.4, doorDepth] as [number, number, number],
            getDetails: () => ({
              'State': door.state.toUpperCase(),
              'Open Progress': `${(door.openProgress * 100).toFixed(0)}%`,
              'Slide Axis': dims.slide_axis.toUpperCase(),
            }),
          }
          if (door.leftMesh) this.debugRegistry?.registerMesh(door.leftMesh, 'door', door.leftMesh.material, doorMeta)
          if (door.rightMesh) this.debugRegistry?.registerMesh(door.rightMesh, 'door', door.rightMesh.material, doorMeta)
        } else if (obj.entity_class === 'CBreakableObject' || obj.tag.includes('CBreakableObject')) {
          const dest = destructibleManager.createCrate(
            {
              id: obj.piece_name || obj.tag,
              position: new THREE.Vector3(objPos.x, 0.5, objPos.z),
              size: [0.9, 0.9, 0.9],
              maxHp: 40,
              lootDrop: 'health-tank',
            },
            this.world,
            RAPIER,
          )
          destructibles.push(dest)
          if (dest.mesh) {
            this.debugRegistry?.registerMesh(dest.mesh, 'destructible', dest.mesh.material, {
              id: obj.piece_name || obj.tag,
              name: `${obj.piece_name || 'Supply'} Crate`,
              sourceTag: `ESOTERIA/AREAS.E3 : ${obj.tag}`,
              dimensions: [0.9, 0.9, 0.9],
              getDetails: () => ({
                'HP': `${dest.hp}/${dest.maxHp}`,
                'Loot Drop': 'health-tank',
              }),
            })
          }
        }
      }
    }

    return {
      spawnPosition,
      facingRad,
      roomMeshes: this.builder.meshes,
      doors,
      destructibles,
      destructibleManager,
      elevators,
      colliders: this.builder.colliders,
      targets,
      messages: data.tutorial_messages ?? [],
    }
  }

  private async loadGenericSector(data: BspLevelData): Promise<BspLoadedLevel> {
    const doors: SlidingDoor[] = []
    const elevators: ElevatorController[] = []
    const targets: THREE.Mesh[] = []
    const destructibleManager = new DestructibleManager(this.scene)
    const destructibles: DestructibleObject[] = []

    const wallMat = createAuthenticWallMaterial()
    const floorMat = createAuthenticFloorMaterial()
    const ceilingMat = createAuthenticCeilingMaterial()

    const origin = data.spawn_point.position
    const spawnPosition = toEngineCoordinates(data.spawn_point.position, origin, this.scale)
    spawnPosition.y += 1.2
    const facingRad = data.spawn_point.facing_rad ?? 0.0

    // Compute room dimensions from mesh_point_cloud bbox
    const bboxMin = data.mesh_point_cloud?.bbox_min ?? [-100, -100, 0]
    const bboxMax = data.mesh_point_cloud?.bbox_max ?? [100, 100, 100]
    const minEng = toEngineCoordinates(bboxMin, origin, this.scale)
    const maxEng = toEngineCoordinates(bboxMax, origin, this.scale)

    const W = Math.max(2.5, Math.abs(maxEng.x - minEng.x))
    const L = Math.max(2.5, Math.abs(maxEng.z - minEng.z))
    const H = Math.max(3.2, Math.abs(maxEng.y - minEng.y))
    const centerX = (minEng.x + maxEng.x) / 2
    const centerZ = (minEng.z + maxEng.z) / 2
    const floorY = Math.min(minEng.y, maxEng.y)
    const ceilY = floorY + H
    const wallY = floorY + H / 2
    const T = 0.35

    // 1. Floor & Ceiling
    this.builder.addFloor({ width: W, length: L, position: { x: centerX, y: floorY, z: centerZ }, material: floorMat, name: `${data.level.toUpperCase()} Floor Panel`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3 (Floor)` })
    this.builder.addCeiling({ width: W, length: L, position: { x: centerX, y: ceilY, z: centerZ }, material: ceilingMat, name: `${data.level.toUpperCase()} Ceiling Panel`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3 (Ceiling)` })

    // 2. 4 Perimeter Walls
    this.builder.addWall({ width: T, height: H, depth: L, position: { x: centerX - W / 2, y: wallY, z: centerZ }, material: wallMat, name: `${data.level.toUpperCase()} West Wall`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3` })
    this.builder.addWall({ width: T, height: H, depth: L, position: { x: centerX + W / 2, y: wallY, z: centerZ }, material: wallMat, name: `${data.level.toUpperCase()} East Wall`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3` })
    this.builder.addWall({ width: W, height: H, depth: T, position: { x: centerX, y: wallY, z: centerZ - L / 2 }, material: wallMat, name: `${data.level.toUpperCase()} North Wall`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3` })
    this.builder.addWall({ width: W, height: H, depth: T, position: { x: centerX, y: wallY, z: centerZ + L / 2 }, material: wallMat, name: `${data.level.toUpperCase()} South Wall`, sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3` })

    // 3. Interior Sector Dividers & Partitions from TREES.E3
    if (data.sectors && data.sectors.length > 0) {
      let partCount = 0
      for (let sIdx = 0; sIdx < data.sectors.length; sIdx++) {
        const sec = data.sectors[sIdx]
        const sMin = toEngineCoordinates(sec.bbox_min, origin, this.scale)
        const sMax = toEngineCoordinates(sec.bbox_max, origin, this.scale)

        const secW = Math.abs(sMax.x - sMin.x)
        const secL = Math.abs(sMax.z - sMin.z)
        const secH = Math.abs(sMax.y - sMin.y)
        const secCX = (sMin.x + sMax.x) / 2
        const secCZ = (sMin.z + sMax.z) / 2
        const secCY = (sMin.y + sMax.y) / 2

        // Identify vertical wall partitions from sector bounding shapes
        const isWallX = secW < 1.4 && secL > 1.2 && secH > 1.2
        const isWallZ = secL < 1.4 && secW > 1.2 && secH > 1.2

        if (isWallX && Math.abs(secCX - (centerX - W / 2)) > 0.5 && Math.abs(secCX - (centerX + W / 2)) > 0.5) {
          partCount++
          this.builder.addWall({
            width: Math.max(0.35, secW),
            height: Math.max(2.5, secH),
            depth: secL,
            position: { x: secCX, y: secCY, z: secCZ },
            material: wallMat,
            name: `${data.level.toUpperCase()} Partition #${partCount}`,
            sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3 (Sector ${sIdx})`,
          })
        } else if (isWallZ && Math.abs(secCZ - (centerZ - L / 2)) > 0.5 && Math.abs(secCZ - (centerZ + L / 2)) > 0.5) {
          partCount++
          this.builder.addWall({
            width: secW,
            height: Math.max(2.5, secH),
            depth: Math.max(0.35, secL),
            position: { x: secCX, y: secCY, z: secCZ },
            material: wallMat,
            name: `${data.level.toUpperCase()} Partition #${partCount}`,
            sourceTag: `ESOTERIA/TREES.E3 : ${data.level}.e3 (Sector ${sIdx})`,
          })
        }
      }
    }

    // 4. Placed Objects (Doors, Crates from AREAS.E3)
    if (data.placed_objects && data.placed_objects.length > 0) {
      for (const obj of data.placed_objects) {
        const objPos = toEngineCoordinates(obj.centroid, origin, this.scale)
        const dimSrc = obj.dimensions_source
        const objW = dimSrc?.door_width ? dimSrc.door_width * this.scale : 2.2
        const objH = dimSrc?.height_z ? dimSrc.height_z * this.scale : 3.4
        const objD = dimSrc?.door_depth ? dimSrc.door_depth * this.scale : 0.35
        const clearance = dimSrc?.clearance_margin ? dimSrc.clearance_margin * this.scale : 0.02
        const axis = dimSrc?.slide_axis ?? 'x'

        if (obj.entity_class === 'CSlidingDoor' || obj.tag.includes('CSlidingDoor')) {
          const door = createDoorInstance(this.scene, this.world, RAPIER, {
            id: obj.piece_name || obj.tag,
            position: new THREE.Vector3(objPos.x, floorY, objPos.z),
            width: Math.max(1.8, objW),
            height: Math.max(2.8, objH),
            depth: Math.max(0.2, objD),
            clearanceMargin: clearance,
            slideAxis: axis,
          })
          doors.push(door)
          const doorMeta = {
            id: obj.piece_name || obj.tag,
            name: `${obj.piece_name || 'Sliding'} Blast Door`,
            sourceTag: `ESOTERIA/AREAS.E3 : ${obj.tag}`,
            dimensions: [objW, objH, 0.35] as [number, number, number],
            getDetails: () => ({
              'State': door.state.toUpperCase(),
              'Open Progress': `${(door.openProgress * 100).toFixed(0)}%`,
              'Slide Axis': axis.toUpperCase(),
            }),
          }
          if (door.leftMesh) {
            this.debugRegistry?.registerMesh(door.leftMesh, 'door', door.leftMesh.material, doorMeta)
          }
          if (door.rightMesh) {
            this.debugRegistry?.registerMesh(door.rightMesh, 'door', door.rightMesh.material, doorMeta)
          }
        } else if (obj.entity_class === 'CBreakableObject' || obj.tag.includes('CBreakableObject')) {
          const dest = destructibleManager.createCrate(
            {
              id: obj.piece_name || obj.tag,
              position: new THREE.Vector3(objPos.x, floorY + 0.45, objPos.z),
              size: [0.9, 0.9, 0.9],
              maxHp: 40,
              lootDrop: 'health-tank',
            },
            this.world,
            RAPIER,
          )
          destructibles.push(dest)
          if (dest.mesh) {
            this.debugRegistry?.registerMesh(dest.mesh, 'destructible', dest.mesh.material, {
              id: obj.piece_name || obj.tag,
              name: `${obj.piece_name || 'Supply'} Crate`,
              sourceTag: `ESOTERIA/AREAS.E3 : ${obj.tag}`,
              dimensions: [0.9, 0.9, 0.9],
              getDetails: () => ({
                'HP': `${dest.hp}/${dest.maxHp}`,
                'Loot Drop': 'health-tank',
              }),
            })
          }
        }
      }
    }

    // 5. Load sector props via GltfPropLoader
    const cleanLevel = (data.level || '').toLowerCase()
    try {
      const gltfProps = await this.propLoader.loadSectorProps(cleanLevel)
      doors.push(...gltfProps.doors)
      destructibles.push(...gltfProps.destructibles)
      elevators.push(...gltfProps.elevators)
      this.builder.colliders.push(...gltfProps.colliders)
      this.builder.meshes.push(...gltfProps.meshes)
    } catch {
      // Sector has no dedicated props bundle, continue with BSP geometry
    }

    return {
      spawnPosition,
      facingRad,
      roomMeshes: this.builder.meshes,
      doors,
      destructibles,
      destructibleManager,
      elevators,
      colliders: this.builder.colliders,
      targets,
      messages: data.tutorial_messages ?? [],
    }
  }
}
